/**
 * 檢視偏好（跟著「人」走，不跟著「檔案」走）的持久化。
 *
 * 與 `.bproj` 的 view 區塊刻意分開：寫進檔案的東西是**專案資料的一部分**，
 * 把檔案給同事，對方開檔就會被我的偏好覆蓋；而且自己每開一個舊檔都要重設一次。
 * 依賴線要畫多少，是看的人當下想怎麼看，與這份專案的內容無關 —— 因此比照主題
 * （見 `theme/theme.ts` 的同一段理由）一律走 `localStorage`。
 *
 * 反過來說，`showBaseline` / `showCriticalPath` / `collapsedTaskIds` / 欄位設定
 * 留在 `.bproj` 是對的：那些描述「這份專案要怎麼呈現」，換人開也應該一樣。
 */

import type { DependencyLineMode } from './types';

export const DEPENDENCY_LINE_MODE_STORAGE_KEY = 'bigint-project.dependencyLineMode';

export const DEFAULT_DEPENDENCY_LINE_MODE: DependencyLineMode = 'all';

export function isDependencyLineMode(value: unknown): value is DependencyLineMode {
  return value === 'all' || value === 'selected' || value === 'none';
}

/**
 * 讀取已儲存的偏好。
 *
 * 讀不到、值不合法、或 `localStorage` 不可用（測試環境、隱私模式）時一律回退
 * 預設值 —— 一個顯示偏好壞掉不該讓整個 app 起不來。
 */
export function readStoredDependencyLineMode(): DependencyLineMode {
  try {
    const raw = globalThis.localStorage?.getItem(DEPENDENCY_LINE_MODE_STORAGE_KEY);
    return isDependencyLineMode(raw) ? raw : DEFAULT_DEPENDENCY_LINE_MODE;
  } catch {
    return DEFAULT_DEPENDENCY_LINE_MODE;
  }
}

export function writeStoredDependencyLineMode(mode: DependencyLineMode): void {
  try {
    globalThis.localStorage?.setItem(DEPENDENCY_LINE_MODE_STORAGE_KEY, mode);
  } catch {
    // 寫入失敗（配額、隱私模式）不影響本次 session 已經套用的設定。
  }
}
